import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, Field, InfoTip, PillButton, apiErrorMessage, collectDisplayYears, displayYearLabel, formatMoney, inDisplayYear, brandLocalDate, useBrand } from '@aralere/shared-ui';
import type { Business, CatalogTaxCategory, Client, CreateExpenseRequest, Expense, ExpenseAttachment, ExpenseLineInput, ExpenseLineItem, ProjectWithTotals, StandardMarginConfig, UpdateExpenseRequest, Vendor } from '@aralere/shared-types';
import { TAX_CATEGORY_OPTIONS, isCatalogTaxCategory } from './taxCategory';

// One-time / general expenses panel (spec §10.6; Stage-3 slice 5 — line-level attribution s56; charge
// model + UX refinements s56.1). A purchase is ONE expense whose LINES each target a client, a
// client's project (forward — slice 6), or ADMIN/OVERHEAD; you later invoice a client by pulling that
// client's billable lines. A billable line REQUIRES a client. Its charge is set at entry: brand
// margin, a manual price, or the cost by default. Brand-scoped (picker collapses at one brand). Per-
// line receipts (image or PDF) upload through the admin-only #3 transport into `receipts`. A per-
// vendor rollup sums line cost/charge across the loaded expenses (v1; full reporting is Stage 8).

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';
const round2 = (n: number): number => Math.round(n * 100) / 100;
const fmtMoney = formatMoney; // shared thousands-separated formatter (s58)
const displayDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return d ? `${m}-${d}-${y}` : iso;
};

/** Client-side mirror of computePrice (preview only; the server is authoritative). Mode-aware:
 *  margin = cost / (1 - rate); markup = cost * (1 + rate); rounding applies in both. */
function marginPrice(cost: number, cfg: StandardMarginConfig): number {
  const mode = cfg.mode ?? 'margin';
  let base: number;
  if (mode === 'markup') {
    base = cost * (1 + cfg.rate);
  } else {
    if (cfg.rate >= 1) return round2(cost);
    base = cost / (1 - cfg.rate);
  }
  if (cfg.rounding === 'whole') return Math.ceil(base - 1e-9);
  const cents = cfg.rounding === '0.49' ? 0.49 : 0.99;
  const whole = Math.floor(base + 1e-9);
  let price = whole + cents;
  if (price < base - 1e-9) price = whole + 1 + cents;
  return round2(price);
}

interface LineForm {
  lineId: string | null; // null = new line
  clientId: string; // '' = admin/overhead
  projectId: string; // '' = none (slice 6); only with a client — the project must belong to that client
  vendorId: string; // '' = none
  category: string;
  description: string;
  cost: string;
  billable: boolean;
  useMargin: boolean;
  charge: string; // '' = bill at cost (when billable, not margin)
  serial: string;
  deliveryDate: string;
  warrantyExpiry: string;
  includeTax: boolean;
  includeShipping: boolean;
  /** Fork 2 / Q47 (slice 4): true (default) = sales tax was paid to the vendor; false = bought
   *  with a resale certificate. Drives the pulled invoice line's taxability. */
  taxPaidAtPurchase: boolean;
  /** 04 #160 WS-B (02i T3.1): WHAT was bought, for sales tax; '' = not set (a pulled line reads as
   *  tangible goods). Only matters on a billable line (it rides onto the invoice line at pull). */
  taxCategory: '' | CatalogTaxCategory;
  date: string;
  billed: boolean; // display-only (locked)
}

// 04 #136: a new line's date prefills to today on the brand clock of the page's brand (02 s177).
const emptyLine = (timezone: string | null): LineForm => ({
  lineId: null,
  clientId: '',
  projectId: '',
  vendorId: '',
  category: '',
  description: '',
  cost: '',
  billable: false,
  useMargin: false,
  charge: '',
  serial: '',
  deliveryDate: '',
  warrantyExpiry: '',
  includeTax: true,
  includeShipping: true,
  taxPaidAtPurchase: true,
  taxCategory: '',
  date: brandLocalDate(timezone),
  billed: false,
});

function lineFormsFromExpense(e: Expense): LineForm[] {
  return e.lineItems.map((l) => ({
    lineId: l.lineId,
    clientId: l.clientId ?? '',
    projectId: l.projectId ?? '',
    vendorId: l.vendorId ?? '',
    category: l.category,
    description: l.description,
    cost: String(l.cost),
    billable: l.billable,
    useMargin: false, // stored charge is concrete; re-opening shows it as a manual value
    charge: l.charge != null ? String(l.charge) : '',
    serial: l.serial ?? '',
    deliveryDate: l.deliveryDate ?? '',
    warrantyExpiry: l.warrantyExpiry ?? '',
    includeTax: l.includeTax ?? true,
    includeShipping: l.includeShipping ?? true,
    taxPaidAtPurchase: l.taxPaidAtPurchase !== false, // absent = true (the default)
    taxCategory: isCatalogTaxCategory(l.taxCategory) ? l.taxCategory : '',
    date: l.date,
    billed: l.billed,
  }));
}

/** Split tax + shipping proportionally by cost across the lines whose respective flag is true; the
 *  last participating line absorbs the rounding remainder. Mirror of api/src/lib/expenses.ts. */
function allocateShares(
  items: { cost: number; includeTax: boolean; includeShipping: boolean }[],
  taxAmount: number | null,
  shippingAmount: number | null,
): { tax: number; shipping: number }[] {
  const shares = items.map(() => ({ tax: 0, shipping: 0 }));
  const split = (amount: number | null, flag: 'includeTax' | 'includeShipping', key: 'tax' | 'shipping'): void => {
    if (!amount || amount <= 0) return;
    const idxs = items.map((l, i) => (l[flag] ? i : -1)).filter((i) => i >= 0);
    const base = idxs.reduce((s, i) => s + items[i].cost, 0);
    if (base <= 0) return;
    let allocated = 0;
    idxs.forEach((i, k) => {
      const share = k === idxs.length - 1 ? round2(amount - allocated) : round2((amount * items[i].cost) / base);
      allocated = round2(allocated + share);
      shares[i][key] = share;
    });
  };
  split(taxAmount, 'includeTax', 'tax');
  split(shippingAmount, 'includeShipping', 'shipping');
  return shares;
}

export function OneTimeExpensesPanel(): JSX.Element {
  const { brand: portalBrand } = useBrand();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [clientFilter, setClientFilter] = useState(''); // '' all | 'admin' | clientId
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [title, setTitle] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [shippingAmount, setShippingAmount] = useState('');
  const [lines, setLines] = useState<LineForm[]>(() => [emptyLine(null)]); // openCreate resets it on the page brand
  const [projectsByClient, setProjectsByClient] = useState<Record<string, ProjectWithTotals[]>>({}); // slice 6 picker cache
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [attachBusyId, setAttachBusyId] = useState<string | null>(null);
  const [attachDeletingId, setAttachDeletingId] = useState<string | null>(null); // the attachmentId being deleted (null during an upload)
  const [expandedOthers, setExpandedOthers] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [b, c, v] = await Promise.all([api.admin.listBusinesses(), api.admin.listClients(), api.admin.listVendors()]);
        if (!alive) return;
        setBusinesses(b.businesses);
        setClients(c.clients.map((r) => r.client));
        setVendors(v.vendors);
        if (b.businesses.length > 0) setBusinessId((cur) => cur || b.businesses[0].id);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (bid: string): Promise<void> => {
    if (!bid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.listExpenses(bid);
      setExpenses(res.expenses);
    } catch (e) {
      setError(apiErrorMessage(e));
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) void load(businessId);
    else setExpenses([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const brandMargin: StandardMarginConfig = useMemo(
    () => businesses.find((b) => b.id === businessId)?.standardMargin ?? { enabled: false, rate: 0, rounding: 'whole' },
    [businesses, businessId],
  );
  // The "use brand ___" label follows the brand's pricing mode (slice 5.3).
  const marginModeLabel = brandMargin.mode === 'markup' ? 'markup' : 'margin';
  const brandClients = useMemo(
    () => clients.filter((c) => c.primaryBusinessId === businessId && c.status !== 'archived'),
    [clients, businessId],
  );
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const vendorById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);
  const activeVendors = useMemo(() => vendors.filter((v) => v.active), [vendors]);
  const clientLabel = (id: string | null): string => (id ? clientById.get(id)?.displayName ?? 'Unknown client' : 'Admin / Overhead');
  const vendorLabel = (id: string | null): string => (id ? vendorById.get(id)?.displayName ?? 'Unknown vendor' : 'No vendor');

  /** s72 year-selector convention: a bill's display date is its LATEST line date (bills usually
   *  carry one purchase date; createdAt is the fallback), and December keeps counting as the
   *  current year through January. The selector renders only when bills span multiple years. */
  const expenseDisplayDate = (e: Expense): string =>
    e.lineItems.reduce((max, l) => (l.date > max ? l.date : max), '') || e.createdAt.slice(0, 10);
  const expenseYears = useMemo(() => collectDisplayYears(expenses.map(expenseDisplayDate)), [expenses]);
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const effectiveYear = expenseYears.includes(yearFilter) ? yearFilter : expenseYears[0] ?? yearFilter;

  /** s72 owner UX: the billing-status view — surface unbilled billable lines (what the §7.11
   *  picker can still pull) or billed ones. Filters combine PER LINE: an expense shows when at
   *  least one line matches BOTH the client and billing filters; the card dims the rest and the
   *  By-vendor rollup counts only matching lines. */
  const [billingFilter, setBillingFilter] = useState<'' | 'unbilled' | 'billed'>('');
  const matchesClient = (l: ExpenseLineItem): boolean =>
    !clientFilter ? true : clientFilter === 'admin' ? l.clientId == null : l.clientId === clientFilter;
  const matchesBilling = (l: ExpenseLineItem): boolean =>
    billingFilter === '' ? true : billingFilter === 'unbilled' ? l.billable && !l.billed : l.billed;
  const matchesFilters = (l: ExpenseLineItem): boolean => matchesClient(l) && matchesBilling(l);
  const lineFilterActive = !!clientFilter || !!billingFilter;

  const visibleExpenses = useMemo(() => {
    const yearScoped =
      expenseYears.length > 1 ? expenses.filter((e) => inDisplayYear(expenseDisplayDate(e), effectiveYear)) : expenses;
    if (!lineFilterActive) return yearScoped;
    return yearScoped.filter((e) => e.lineItems.some(matchesFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, clientFilter, billingFilter, expenseYears, effectiveYear]);

  // Invoice-line deep link (s77 #16b): ?client= scopes; ?focus=<expenseId|lineId> reveals + highlights
  // the expense that a billed line came from. The focus can be either the doc id or a sub-line id.
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  useEffect(() => {
    const c = searchParams.get('client');
    if (c) setClientFilter(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const focusExpense = focusId
    ? expenses.find((e) => e.id === focusId || e.lineItems.some((l) => l.lineId === focusId))
    : undefined;
  useEffect(() => {
    if (focusId && focusExpense) setYearFilter(new Date(expenseDisplayDate(focusExpense)).getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, focusExpense?.id]);
  useEffect(() => {
    if (!focusId || loading || !focusExpense) return;
    if (!visibleExpenses.some((e) => e.id === focusExpense.id)) return;
    document.getElementById('focus-expense')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, loading, visibleExpenses]);

  const rollup = useMemo(() => {
    const by = new Map<string, { name: string; lineCount: number; cost: number; billable: number }>();
    for (const e of visibleExpenses) {
      for (const l of e.lineItems) {
        if (!matchesFilters(l)) continue; // the rollup mirrors the line-level view
        const key = l.vendorId ?? '__none__';
        const row = by.get(key) ?? { name: l.vendorId ? vendorLabel(l.vendorId) : 'No vendor', lineCount: 0, cost: 0, billable: 0 };
        row.lineCount += 1;
        row.cost += l.cost;
        if (l.billable) row.billable += l.charge ?? 0;
        by.set(key, row);
      }
    }
    return [...by.values()].map((r) => ({ ...r, cost: round2(r.cost), billable: round2(r.billable) })).sort((a, b) => b.cost - a.cost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleExpenses, vendorById, clientFilter, billingFilter]);

  /** 04 #136: the page brand's timezone (its `timezone`, absent = the platform default); with no
   *  page brand yet, the portal brand's. The expense belongs to the page brand. */
  const pageTz = (): string | null => {
    const b = businesses.find((x) => x.id === businessId);
    return b ? b.timezone ?? null : portalBrand?.timezone ?? null;
  };

  const openCreate = (): void => {
    setEditing(null);
    setTitle('');
    setInvoiceNumber('');
    setTaxAmount('');
    setShippingAmount('');
    setLines([emptyLine(pageTz())]);
    setFormError(null);
    setFormOpen(true);
  };
  const openEdit = (e: Expense): void => {
    setEditing(e);
    setTitle(e.title);
    setInvoiceNumber(e.invoiceNumber ?? '');
    setTaxAmount(e.taxAmount != null ? String(e.taxAmount) : '');
    setShippingAmount(e.shippingAmount != null ? String(e.shippingAmount) : '');
    setLines(lineFormsFromExpense(e));
    setFormError(null);
    setFormOpen(true);
  };
  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const updateLine = (idx: number, patch: Partial<LineForm>): void =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = (): void => setLines((ls) => [emptyLine(pageTz()), ...ls]); // new line on top
  const removeLine = (idx: number): void => setLines((ls) => ls.filter((_, i) => i !== idx));

  // Slice 6: lazy-load each line client's projects for the Project picker (cached per client;
  // best-effort — a projects hiccup must not block expense entry).
  const lineClientKey = lines
    .map((l) => l.clientId)
    .concat(expenses.flatMap((e) => e.lineItems.filter((l) => l.projectId).map((l) => l.clientId ?? '')))
    .filter(Boolean)
    .join(',');
  useEffect(() => {
    const wanted = new Set<string>();
    for (const l of lines) if (l.clientId) wanted.add(l.clientId);
    for (const e of expenses) for (const l of e.lineItems) if (l.projectId && l.clientId) wanted.add(l.clientId);
    const missing = Array.from(wanted).filter((cid) => !projectsByClient[cid]);
    if (missing.length === 0) return;
    let alive = true;
    void (async () => {
      for (const cid of missing) {
        try {
          const res = await api.admin.listProjects(cid);
          if (alive) setProjectsByClient((m) => ({ ...m, [cid]: res.projects }));
        } catch {
          if (alive) setProjectsByClient((m) => ({ ...m, [cid]: [] }));
        }
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineClientKey]);

  const toInput = (l: LineForm): ExpenseLineInput => ({
    lineId: l.lineId ?? undefined,
    clientId: l.clientId || null,
    projectId: l.clientId && l.projectId ? l.projectId : null,
    vendorId: l.vendorId || null,
    category: l.category.trim(),
    description: l.description.trim(),
    cost: Math.round((parseFloat(l.cost) || 0) * 100) / 100,
    billable: l.billable,
    useMargin: l.billable && l.useMargin,
    charge: l.billable && !l.useMargin && l.charge.trim() ? Math.round((parseFloat(l.charge) || 0) * 100) / 100 : null,
    serial: l.serial.trim() || null,
    deliveryDate: l.deliveryDate || null,
    warrantyExpiry: l.warrantyExpiry || null,
    includeTax: l.includeTax,
    includeShipping: l.includeShipping,
    taxPaidAtPurchase: l.taxPaidAtPurchase,
    taxCategory: l.taxCategory === '' ? null : l.taxCategory,
    date: l.date,
  });

  const doSave = async (): Promise<void> => {
    if (!title.trim()) return setFormError('An expense title is required.');
    if (!invoiceNumber.trim()) return setFormError('An invoice number is required.');
    const parseAmt = (s: string): number | null => (s.trim() ? Math.round((parseFloat(s) || 0) * 100) / 100 : null);
    const tax = parseAmt(taxAmount);
    const ship = parseAmt(shippingAmount);
    if ((tax != null && tax < 0) || (ship != null && ship < 0)) return setFormError('Tax and shipping must be 0 or more.');
    if (lines.length === 0) return setFormError('Add at least one line.');
    for (const l of lines) {
      if (l.billed) continue;
      if (!l.category.trim() || !l.description.trim()) return setFormError('Every line needs a category and a description.');
      const cost = parseFloat(l.cost);
      if (!l.cost.trim() || !Number.isFinite(cost) || cost < 0) return setFormError('Every line needs a valid cost (0 or more).');
      if (!l.date) return setFormError('Every line needs a date.');
      if (l.billable && l.clientId === '') return setFormError('A billable line needs a client. Pick a client or turn off billable.');
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const body: UpdateExpenseRequest = { title: title.trim(), invoiceNumber: invoiceNumber.trim(), taxAmount: tax, shippingAmount: ship, lineItems: lines.map(toInput) };
        await api.admin.updateExpense(editing.id, editing.businessId, body);
      } else {
        const body: CreateExpenseRequest = { businessId, title: title.trim(), invoiceNumber: invoiceNumber.trim(), taxAmount: tax, shippingAmount: ship, lineItems: lines.map(toInput) };
        await api.admin.createExpense(body);
      }
      closeForm();
      await load(businessId);
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.admin.deleteExpense(deleteTarget.id, deleteTarget.businessId);
      setDeleteTarget(null);
      await load(businessId);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const attachInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const applyAttachments = (expenseId: string, attachments: ExpenseAttachment[]): void => {
    setExpenses((xs) => xs.map((x) => (x.id === expenseId ? { ...x, attachments } : x)));
    setEditing((cur) => (cur && cur.id === expenseId ? { ...cur, attachments } : cur));
  };
  const onUploadAttachment = async (expense: Expense, file: File | null): Promise<void> => {
    if (!file) return;
    setAttachBusyId(expense.id);
    setAttachDeletingId(null);
    setError(null);
    try {
      const res = await api.admin.addExpenseAttachment(expense.id, expense.businessId, file);
      applyAttachments(expense.id, res.attachments);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAttachBusyId(null);
    }
  };
  const onRemoveAttachment = async (expense: Expense, attachmentId: string): Promise<void> => {
    setAttachBusyId(expense.id);
    setAttachDeletingId(attachmentId);
    setError(null);
    try {
      const res = await api.admin.deleteExpenseAttachment(expense.id, expense.businessId, attachmentId);
      applyAttachments(expense.id, res.attachments);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAttachBusyId(null);
      setAttachDeletingId(null);
    }
  };

  // The header "Attachments" section (bill-level, many files) — used in the card and, when editing,
  // in the form. Files can only attach to a SAVED expense.
  const renderAttachments = (e: Expense): JSX.Element => {
    const atts = e.attachments ?? [];
    const busy = attachBusyId === e.id;
    const uploading = busy && attachDeletingId === null;
    return (
      <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">Attachments <span className="text-[var(--color-muted)]">(receipts, invoices)</span></span>
          <input
            ref={(el) => { attachInputs.current[e.id] = el; }}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={(ev) => {
              const f = ev.target.files?.[0] ?? null;
              ev.target.value = '';
              void onUploadAttachment(e, f);
            }}
          />
          <PillButton onClick={() => attachInputs.current[e.id]?.click()} disabled={busy}>{uploading ? 'Uploading...' : 'Add file'}</PillButton>
        </div>
        {atts.length === 0 ? (
          <p className="text-xs text-[var(--color-muted)]">No files attached.</p>
        ) : (
          <ul className="space-y-1">
            {atts.map((a) => (
              <li key={a.attachmentId} className="flex items-center justify-between gap-2 text-sm">
                <a href={api.admin.expenseAttachmentUrl(e.id, e.businessId, a.attachmentId)} target="_blank" rel="noopener noreferrer" className="truncate text-[var(--link-text)] hover:underline">{a.filename}</a>
                <button type="button" disabled={busy} className="shrink-0 text-xs font-medium text-[var(--tint-red-text)] hover:underline disabled:opacity-60" onClick={() => void onRemoveAttachment(e, a.attachmentId)}>{attachDeletingId === a.attachmentId ? 'Deleting...' : 'Remove'}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const expenseTotal = (e: Expense): number => round2(e.lineItems.reduce((s, l) => s + l.cost, 0));
  const hasBilled = (e: Expense): boolean => e.lineItems.some((l) => l.billed);

  // The line's effective client charge, for the inline preview.
  const chargePreview = (l: LineForm): string => {
    const cost = parseFloat(l.cost) || 0;
    if (l.useMargin && brandMargin.enabled) return `Client pays ${fmtMoney(marginPrice(cost, brandMargin))} (${marginModeLabel})`;
    if (l.charge.trim()) return `Client pays ${fmtMoney(parseFloat(l.charge) || 0)}`;
    return `Client pays ${fmtMoney(cost)} (at cost)`;
  };

  const renderForm = (): JSX.Element => {
    const taxNum = taxAmount.trim() ? Math.round((parseFloat(taxAmount) || 0) * 100) / 100 : null;
    const shipNum = shippingAmount.trim() ? Math.round((parseFloat(shippingAmount) || 0) * 100) / 100 : null;
    const hasTaxOrShip = taxNum != null || shipNum != null;
    const formShares = allocateShares(
      lines.map((l) => ({ cost: parseFloat(l.cost) || 0, includeTax: l.includeTax, includeShipping: l.includeShipping })),
      taxNum,
      shipNum,
    );
    return (
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl">{editing ? 'Edit Expense' : 'New Expense'}</h2>
          <button type="button" className="text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]" onClick={closeForm}>Close</button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Title" required>
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Amazon order, new hire kit" />
          </Field>
          <Field label="Invoice number" required>
            <input className={inputClass} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. 114-2258" />
          </Field>
          <Field label="Tax ($)">
            <input type="number" step="any" min="0" className={inputClass} value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} placeholder="optional" />
          </Field>
          <Field label="Shipping ($)">
            <input type="number" step="any" min="0" className={inputClass} value={shippingAmount} onChange={(e) => setShippingAmount(e.target.value)} placeholder="optional" />
          </Field>
        </div>
        {hasTaxOrShip ? (
          <p className="mt-1 text-xs text-[var(--color-muted)]">Tax and shipping split across the lines you check below, in proportion to each line's cost.</p>
        ) : null}

        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Line items</h3>
            <Button type="button" variant="secondary" onClick={addLine}>Add line</Button>
          </div>

          {lines.map((l, idx) => (
            <div key={l.lineId ?? `new-${idx}`} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
              {l.billed ? (
                <div className="mb-2 text-xs font-medium text-[var(--color-muted)]">This line is billed and locked.</div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Client">
                  <select
                    className={inputClass}
                    value={l.clientId}
                    disabled={l.billed}
                    onChange={(ev) => {
                      const cid = ev.target.value;
                      updateLine(idx, cid === '' ? { clientId: '', projectId: '', billable: false, useMargin: false } : { clientId: cid, projectId: '' });
                    }}
                  >
                    <option value="">Admin / Overhead</option>
                    {brandClients.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                  </select>
                </Field>
                {l.clientId !== '' ? (
                  <Field label="Project" tip="Optional. Ties this line to one of the client's projects so the project's totals and (Stage 4) 'Generate Invoice from Project' pick it up. Only active projects accept new entries.">
                    <select className={inputClass} value={l.projectId} disabled={l.billed} onChange={(ev) => updateLine(idx, { projectId: ev.target.value })}>
                      <option value="">None</option>
                      {(projectsByClient[l.clientId] ?? [])
                        .filter((pr) => pr.status === 'active' || pr.id === l.projectId)
                        .map((pr) => (
                          <option key={pr.id} value={pr.id}>{pr.name}{pr.status !== 'active' ? ` (${pr.status === 'on-hold' ? 'on hold' : pr.status})` : ''}</option>
                        ))}
                    </select>
                  </Field>
                ) : null}
                <Field label="Vendor">
                  <select className={inputClass} value={l.vendorId} disabled={l.billed} onChange={(ev) => updateLine(idx, { vendorId: ev.target.value })}>
                    <option value="">No vendor</option>
                    {l.vendorId && !activeVendors.some((v) => v.id === l.vendorId) ? (
                      <option value={l.vendorId}>{vendorLabel(l.vendorId)} (inactive)</option>
                    ) : null}
                    {activeVendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.displayName}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Category" required>
                  <input className={inputClass} value={l.category} disabled={l.billed} onChange={(ev) => updateLine(idx, { category: ev.target.value })} placeholder="hardware" />
                </Field>
                <Field label="Description" required>
                  <input className={inputClass} value={l.description} disabled={l.billed} onChange={(ev) => updateLine(idx, { description: ev.target.value })} placeholder="USB-C hubs" />
                </Field>
                <Field label="Cost ($)" required>
                  <input type="number" step="any" min="0" className={inputClass} value={l.cost} disabled={l.billed} onChange={(ev) => updateLine(idx, { cost: ev.target.value })} />
                </Field>
                <Field label="Date" required>
                  <input type="date" className={inputClass} value={l.date} disabled={l.billed} onChange={(ev) => updateLine(idx, { date: ev.target.value })} />
                </Field>
                <Field label="Serial">
                  <input className={inputClass} value={l.serial} disabled={l.billed} onChange={(ev) => updateLine(idx, { serial: ev.target.value })} placeholder="optional" />
                </Field>
                <Field label="Delivery date">
                  <input type="date" className={inputClass} value={l.deliveryDate} disabled={l.billed} onChange={(ev) => updateLine(idx, { deliveryDate: ev.target.value })} />
                </Field>
                <Field label="Warranty expires">
                  <input type="date" className={inputClass} value={l.warrantyExpiry} disabled={l.billed} onChange={(ev) => updateLine(idx, { warrantyExpiry: ev.target.value })} />
                </Field>
              </div>

              {/* Billable only applies when the line is tied to a client. */}
              {l.clientId !== '' ? (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={l.billable} disabled={l.billed} onChange={(ev) => updateLine(idx, ev.target.checked ? { billable: true } : { billable: false, useMargin: false })} />
                    Billable to the client
                    <InfoTip text="A billable line can be pulled onto this client's invoice. Leave off for a cost you won't recover from them." />
                  </label>
                  {l.billable ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6">
                      {brandMargin.enabled ? (
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={l.useMargin} disabled={l.billed} onChange={(ev) => updateLine(idx, { useMargin: ev.target.checked })} />
                          Use brand {marginModeLabel} ({Math.round(brandMargin.rate * 100)}%)
                        </label>
                      ) : null}
                      {!l.useMargin ? (
                        <label className="flex items-center gap-2 text-sm">
                          <span>Charge ($)</span>
                          <input type="number" step="any" min="0" className="w-32 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]" value={l.charge} disabled={l.billed} onChange={(ev) => updateLine(idx, { charge: ev.target.value })} placeholder="bill at cost" />
                          <InfoTip text={`Leave blank to bill at your cost. Enter a number for a manual price. Or tick 'Use brand ${marginModeLabel}' to price it automatically from the brand's ${marginModeLabel}.`} />
                        </label>
                      ) : null}
                      <span className="text-xs text-[var(--color-muted)]">{chargePreview(l)}</span>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={l.taxPaidAtPurchase} disabled={l.billed} onChange={(ev) => updateLine(idx, { taxPaidAtPurchase: ev.target.checked })} />
                        Tax paid at purchase
                        <InfoTip text="Checked (the default): you paid sales tax to the vendor, so when this line is pulled onto an invoice it is excluded from client tax, with a notation, and you can re-include it there. Uncheck if you bought with a resale certificate; the client sale then charges the tax instead." />
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <span>Tax category</span>
                        <select className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)]" value={l.taxCategory} disabled={l.billed} onChange={(ev) => updateLine(idx, { taxCategory: ev.target.value as '' | CatalogTaxCategory })}>
                          <option value="">Not set (tangible goods when billed)</option>
                          {TAX_CATEGORY_OPTIONS.map(([v, label]) => (
                            <option key={v} value={v}>{label}</option>
                          ))}
                        </select>
                        <InfoTip text="What this line sells for sales-tax purposes when it is billed to the client: left unset, a purchase counts as tangible goods. Pick prewritten software / SaaS for a resold licence, or a service category for labour you bought in. Taxability itself is decided per client and per invoice date from this category and the client's state (from the January 2027 build)." />
                      </label>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Tax/shipping participation — only when the expense carries tax or shipping. */}
              {hasTaxOrShip ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  {taxNum != null ? (
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={l.includeTax} disabled={l.billed} onChange={(ev) => updateLine(idx, { includeTax: ev.target.checked })} />
                      Apply tax{l.includeTax && formShares[idx].tax > 0 ? ` (${fmtMoney(formShares[idx].tax)})` : ''}
                    </label>
                  ) : null}
                  {shipNum != null ? (
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={l.includeShipping} disabled={l.billed} onChange={(ev) => updateLine(idx, { includeShipping: ev.target.checked })} />
                      Apply shipping{l.includeShipping && formShares[idx].shipping > 0 ? ` (${fmtMoney(formShares[idx].shipping)})` : ''}
                    </label>
                  ) : null}
                </div>
              ) : null}

              {!l.billed && lines.length > 1 ? (
                <div className="mt-3 flex">
                  <button type="button" className="ml-auto text-xs font-medium text-[var(--tint-red-text)] hover:underline" onClick={() => removeLine(idx)}>Remove line</button>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Attachments live on a saved expense. */}
        <div className="mt-4">
          {editing ? renderAttachments(editing) : (
            <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-3 text-xs text-[var(--color-muted)]">Save the expense first, then attach receipts or invoices to it.</p>
          )}
        </div>

        {formError ? <p className="mt-3 text-sm text-[var(--tint-red-text)]">{formError}</p> : null}
        <div className="mt-4 flex gap-2">
          <Button onClick={doSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save changes' : 'Create expense'}</Button>
          <Button variant="ghost" onClick={closeForm} disabled={saving}>Cancel</Button>
        </div>
      </Card>
    );
  };

  const toggleOthers = (id: string): void =>
    setExpandedOthers((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const renderExpenseCard = (e: Expense): JSX.Element => {
    const shares = allocateShares(
      e.lineItems.map((l) => ({ cost: l.cost, includeTax: l.includeTax ?? false, includeShipping: l.includeShipping ?? false })),
      e.taxAmount ?? null,
      e.shippingAmount ?? null,
    );
    const shareOf = (l: ExpenseLineItem): { tax: number; shipping: number } => shares[e.lineItems.indexOf(l)] ?? { tax: 0, shipping: 0 };
    const matchLine = matchesFilters;
    const shown = lineFilterActive ? e.lineItems.filter(matchLine) : e.lineItems;
    const others = lineFilterActive ? e.lineItems.filter((l) => !matchLine(l)) : [];
    const expanded = expandedOthers.has(e.id);

    const renderLine = (l: ExpenseLineItem, dim: boolean): JSX.Element => {
      const s = shareOf(l);
      const metaParts = [
        l.serial ? <span><span className="font-semibold text-[var(--color-text)]">Serial</span> {l.serial}</span> : null,
        l.deliveryDate ? <span><span className="font-semibold text-[var(--color-text)]">Delivered</span> {displayDate(l.deliveryDate)}</span> : null,
        l.warrantyExpiry ? <span><span className="font-semibold text-[var(--color-text)]">Warranty</span> to {displayDate(l.warrantyExpiry)}</span> : null,
      ].filter((x): x is JSX.Element => x !== null);
      return (
        <div key={l.lineId} className={`rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-3 text-sm ${dim ? 'opacity-50' : ''}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-medium">{l.description}</div>
              <div className="text-xs text-[var(--color-muted)]">
                <span className="font-semibold text-[var(--color-text)]">{clientLabel(l.clientId)}</span> · {vendorLabel(l.vendorId)} · {l.category} · {displayDate(l.date)}{l.projectId && l.clientId ? <> · <span className="font-semibold text-[var(--color-text)]">Project: {(projectsByClient[l.clientId] ?? []).find((pr) => pr.id === l.projectId)?.name ?? '(loading)'}</span></> : null}{l.billed ? ' · billed' : ''}
              </div>
              {metaParts.length > 0 ? (
                <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                  {metaParts.map((p, i) => <span key={i}>{i > 0 ? ' · ' : ''}{p}</span>)}
                </div>
              ) : null}
            </div>
            <div className="text-right">
              <div>{fmtMoney(l.cost)}</div>
              <div className="text-xs text-[var(--color-muted)]">{l.billable ? `bill ${l.charge != null ? fmtMoney(l.charge) : 'at cost'}` : 'not billable'}</div>
              {s.tax > 0 || s.shipping > 0 ? (
                <div className="text-xs text-[var(--color-muted)]">
                  {s.tax > 0 ? `+${fmtMoney(s.tax)} tax` : ''}{s.tax > 0 && s.shipping > 0 ? ' · ' : ''}{s.shipping > 0 ? `+${fmtMoney(s.shipping)} ship` : ''}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    };

    return (
      <Card key={e.id}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-medium">{e.title}</div>
            <div className="text-xs text-[var(--color-muted)]">
              Invoice {e.invoiceNumber || '-'} · {displayDate(e.createdAt.slice(0, 10))} · {e.lineItems.length} line{e.lineItems.length === 1 ? '' : 's'} · {fmtMoney(expenseTotal(e))}
              {e.taxAmount != null && e.taxAmount > 0 ? ` · tax ${fmtMoney(e.taxAmount)}` : ''}
              {e.shippingAmount != null && e.shippingAmount > 0 ? ` · ship ${fmtMoney(e.shippingAmount)}` : ''}
              {hasBilled(e) ? ' · has billed lines' : ''}
            </div>
          </div>
          <div className="flex gap-2">
            <PillButton onClick={() => openEdit(e)}>Edit</PillButton>
            {hasBilled(e) ? (
              <span className="text-xs text-[var(--color-muted)]">billed (can't delete)</span>
            ) : (
              <PillButton tone="red" onClick={() => setDeleteTarget(e)}>Delete</PillButton>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-2">
          {shown.map((l) => renderLine(l, false))}
          {others.length > 0 ? (
            <div className="space-y-2">
              <button type="button" className="text-xs font-medium text-[var(--link-text)] hover:underline" onClick={() => toggleOthers(e.id)}>
                {expanded ? 'Hide other clients\u2019 lines' : `+${others.length} line${others.length === 1 ? '' : 's'} for other clients`}
              </button>
              {expanded ? others.map((l) => renderLine(l, true)) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-3">{renderAttachments(e)}</div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-3xl text-xs text-[var(--color-muted)]">
          One-time and general purchases. Each line is coded to a client, a client's project (coming
          soon), or admin overhead. A line billed to a client can be pulled onto their invoice. One
          purchase is one expense, even when its items span several clients.
        </p>
        <Button type="button" onClick={openCreate} disabled={!businessId}>New expense</Button>
      </div>

      <Card>
        <div className="grid gap-4 md:grid-cols-2">
          {businesses.length > 1 ? (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Brand</span>
              <select
                className={inputClass}
                value={businessId}
                onChange={(e) => {
                  setBusinessId(e.target.value);
                  setClientFilter('');
                  closeForm();
                }}
              >
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Filter by client</span>
            <select className={inputClass} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <option value="">All expenses</option>
              <option value="admin">Admin / Overhead</option>
              {brandClients.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Billing</span>
            <select className={inputClass} value={billingFilter} onChange={(e) => setBillingFilter(e.target.value as '' | 'unbilled' | 'billed')}>
              <option value="">All lines</option>
              <option value="unbilled">Unbilled billable</option>
              <option value="billed">Billed</option>
            </select>
          </label>
          {expenseYears.length > 1 ? (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Year</span>
              <select className={inputClass} value={String(effectiveYear)} onChange={(e) => setYearFilter(Number(e.target.value))}>
                {expenseYears.map((y) => (
                  <option key={y} value={String(y)}>{displayYearLabel(y)}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </Card>

      {error ? <div className="rounded-md border border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}

      {rollup.length > 0 ? (
        <Card>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="font-display text-lg">By vendor</h2>
            <InfoTip text="Line cost and billable charge grouped by vendor across the expenses shown. A running tally; full reporting comes later." />
          </div>
          <div className="overflow-hidden rounded-md border border-[rgb(var(--edge-rgb)/0.10)]">
            <table className="w-full text-sm">
              <thead className="bg-[rgb(var(--edge-rgb)/0.03)] text-left text-xs text-[var(--color-muted)]">
                <tr>
                  <th className="px-3 py-2 font-medium">Vendor</th>
                  <th className="px-3 py-2 text-right font-medium">Lines</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                  <th className="px-3 py-2 text-right font-medium">Billable</th>
                </tr>
              </thead>
              <tbody>
                {rollup.map((r) => (
                  <tr key={r.name} className="border-t border-[rgb(var(--edge-rgb)/0.05)]">
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-right">{r.lineCount}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(r.cost)}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(r.billable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {/* New-expense form opens here, right below the rollup. */}
      {formOpen && !editing ? renderForm() : null}

      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading expenses...</p> : null}
      {!loading && visibleExpenses.length === 0 ? (
        <Card><p className="text-sm text-[var(--color-muted)]">No one-time expenses{clientFilter ? ' match this filter' : ' yet'}.</p></Card>
      ) : null}

      {/* Expense list (newest first). Editing an expense swaps its card for the form in place. */}
      <div className="space-y-4">
        {visibleExpenses.map((e) => {
          const isFocus = !!focusExpense && e.id === focusExpense.id;
          const body = editing && editing.id === e.id ? <div>{renderForm()}</div> : renderExpenseCard(e);
          return (
            <div key={e.id} id={isFocus ? 'focus-expense' : undefined} className={isFocus ? 'scroll-mt-24 rounded-xl ring-2 ring-[var(--color-primary)]' : undefined}>
              {body}
            </div>
          );
        })}
      </div>

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title="Delete expense?"
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>Delete "{deleteTarget.title}" and its {deleteTarget.lineItems.length} line{deleteTarget.lineItems.length === 1 ? '' : 's'} (and its attachments)? This cannot be undone.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
